import { useState, useMemo } from 'react';
import { Search, LayoutGrid, Star, Folder, ChevronDown, ChevronRight } from 'lucide-react';
import { AppTool, ToolWidget } from './ToolWidget';
import { useAppLauncherStore } from '../../store/appLauncherStore';

interface AppLauncherEngineProps {
  hubId: string;
  hubName: string;
  tools: AppTool[];
}

export function AppLauncherEngine({ hubId, hubName, tools }: AppLauncherEngineProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>({});
  const { pinnedTools } = useAppLauncherStore();
  
  const pins = pinnedTools[hubId] || [];

  const toggleCategoryCollapse = (category: string) => {
    setCollapsedCategories(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const { favorites, categorizedTools } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const filtered = tools.filter(t => 
      t.name.toLowerCase().includes(query) || 
      t.description.toLowerCase().includes(query)
    );

    const favs = filtered.filter(t => pins.includes(t.id));
    
    // Group remaining (unpinned) tools by category
    const unpinned = filtered.filter(t => !pins.includes(t.id));
    const categories = unpinned.reduce((acc, tool) => {
      const cat = tool.category || 'Other Tools';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(tool);
      return acc;
    }, {} as Record<string, AppTool[]>);

    return { favorites: favs, categorizedTools: categories };
  }, [tools, pins, searchQuery]);

  return (
    <div className="flex-1 flex flex-col h-full bg-background overflow-hidden">
      
      {/* Search Header Card */}
      <div className="p-6 pb-0 shrink-0">
        <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 bg-card border border-border p-6 rounded-3xl shadow-sm">
          <div className="flex items-center gap-3">
            <div className="size-10 rounded-2xl bg-primary/10 border border-primary/20 text-primary flex items-center justify-center shrink-0">
              <LayoutGrid className="size-5" />
            </div>
            <div>
              <h1 className="tracking-tight font-display text-page-title text-foreground">{hubName} Hub</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Access {tools.length} apps and modules across your workspace.
              </p>
            </div>
          </div>
          
          <div className="relative w-full sm:w-80 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
            <input
              type="text"
              placeholder={`Search ${hubName.toLowerCase()} apps...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-9 pl-9 pr-4 rounded-xl border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary transition-colors text-xs text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto p-6 custom-scrollbar space-y-8">
        
        {favorites.length > 0 && (
          <section>
            <div className="flex items-center gap-2 mb-4 text-foreground/80">
              <Star className="h-5 w-5 fill-warning text-warning" />
              <h2 className="tracking-tight font-display text-section-title">Pinned Favorites</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {favorites.map(tool => (
                <ToolWidget key={tool.id} hubId={hubId} tool={tool} />
              ))}
            </div>
          </section>
        )}

        {Object.entries(categorizedTools).map(([category, catTools]) => {
          if (catTools.length === 0) return null;
          const isCollapsed = Boolean(collapsedCategories[category]);

          return (
            <section key={category} className="space-y-4">
              <button
                onClick={() => toggleCategoryCollapse(category)}
                className="flex items-center gap-2 text-foreground/80 hover:text-foreground group transition-colors focus:outline-none select-none text-left"
              >
                <div className="p-1 rounded-md hover:bg-accent transition-colors">
                  {isCollapsed ? (
                    <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-muted-foreground group-hover:text-foreground" />
                  )}
                </div>
                <Folder className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                <h2 className="text-lg font-semibold tracking-tight">{category}</h2>
                <span className="text-xs bg-muted px-2.5 py-0.5 rounded-full font-semibold text-muted-foreground">
                  {catTools.length}
                </span>
                <span className="text-[11px] text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity ml-1">
                  {isCollapsed ? '(Click to expand)' : '(Click to collapse)'}
                </span>
              </button>

              {!isCollapsed && (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 animate-in fade-in duration-200">
                  {catTools.map(tool => (
                    <ToolWidget key={tool.id} hubId={hubId} tool={tool} />
                  ))}
                </div>
              )}
            </section>
          );
        })}

        {favorites.length === 0 && Object.keys(categorizedTools).length === 0 && (
          <div className="h-40 flex flex-col items-center justify-center text-muted-foreground">
            <LayoutGrid className="h-10 w-10 mb-3 opacity-20" />
            <p>No applications found matching "{searchQuery}".</p>
          </div>
        )}
      </div>
    </div>
  );
}
